import {
    ChangeDetectionStrategy,
    Component,
    contentChildren,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    viewChild
} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {AppData, ColorPairData} from "../../configuration/app-entity.model";
import {PageFooterControlDirective} from "../page-footer-control.directive";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {ThemeModel} from '../../theme/theme.model';
import {FooterMessagesComponent} from "../../notification/footer-messages/footer-messages.component";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {LogoComponent} from "../../ui-elements/logo/logo.component";
import {BackgroundImageDirective} from '../../ui-elements/image/background-image.directive';
import {SwipableDirective} from '../../ui-elements/swipable.directive';
import {NavigationService} from '../../navigation/navigation.service';
import {Page, PageAlignment, PageLayout} from '../../navigation/page';
import {ConfigurationModel} from '../../configuration/configuration.model';

@Component({
    selector: "woa-full-page-layout",
    templateUrl: "full-page-layout.component.html",
    styleUrls: ["./full-page-layout.component.scss", "../../ui-elements/image/background-image.scss"],
    providers: [{provide: PageLayout, useExisting: FullPageLayoutComponent}],
    imports: [
        BackgroundImageDirective,
        FooterMessagesComponent,
        IconButtonComponent,
        IconComponent,
        LogoComponent,
        NgClass,
        ResponsiveDirective,
        SwipableDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FullPageLayoutComponent extends PageLayout implements OnInit {
    public page: Page = inject(Page);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public navigationService: NavigationService = inject(NavigationService);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private themeModel: ThemeModel = inject(ThemeModel);

    // navbar options
    public readonly showNavbar: InputSignal<boolean> = input<boolean>(true);
    public readonly showNavbarLogo: InputSignal<boolean> = input<boolean>(undefined);
    public readonly autoDismissOnBack: InputSignal<boolean> = input<boolean>(true);
    public readonly hideBackButton: InputSignal<boolean> = input<boolean>(false);
    public readonly makeFloatingBackButtonCircular: InputSignal<boolean> = input<boolean>(true);
    public readonly showBackButtonOnDesktop: InputSignal<boolean> = input<boolean>(true);
    public readonly showRefreshButton: InputSignal<boolean> = input<boolean>(false);
    public readonly enableSwipeRefresh: InputSignal<boolean> = input<boolean>(false);
    public readonly showTitles: InputSignal<boolean> = input<boolean>(true);
    public readonly showSubtitle: InputSignal<boolean> = input<boolean>(true);
    public readonly navbarTitle: InputSignal<string> = input<string>();
    public readonly title: InputSignal<string> = input<string>();
    public readonly subtitle: InputSignal<string> = input<string>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>({});
    public readonly transparent: InputSignal<boolean> = input<boolean>(false);
    public readonly showLoginBackgroundImage: InputSignal<boolean> = input<boolean>(false);
    public readonly backgroundColorPair: InputSignal<string> = input<string>("standard-page");

    // page content options
    public readonly pillarboxedOnMobile: InputSignal<"mobile" | "none"> = input<"mobile" | "none">("mobile");
    public readonly pillarboxedOnDesktop: InputSignal<"mobile" | "desktop" | "double-mobile" | "none"> = input<"mobile" | "desktop" | "double-mobile" | "none">("mobile");
    public readonly showContent: InputSignal<boolean> = input<boolean>(true);
    public readonly contentLocked: InputSignal<boolean> = input<boolean>(false);
    public readonly showFooter: InputSignal<boolean> = input<boolean>(true);
    public readonly showFooterShadow: InputSignal<boolean> = input<boolean>(undefined);
    public readonly footerControlOrientation: InputSignal<"horizontal" | "vertical"> = input<"horizontal" | "vertical">("vertical");
    public readonly allowSafeArea: InputSignal<boolean> = input<boolean>(true);

    public onBack: OutputEmitterRef<void> = output<void>();
    public onRefresh: OutputEmitterRef<void> = output<void>();

    public override pageScrollerER: Signal<ElementRef> = viewChild.required("scroller");
    public footerControls: Signal<ReadonlyArray<PageFooterControlDirective>> = contentChildren(PageFooterControlDirective);

    IconButtonType = IconButtonType;

    private scrollPositionStack: number[] = [];

    ngOnInit(): void {
        this.page.containerComponentRef && this.page.containerComponentRef.instance.geometry.set({alignment: PageAlignment.FULL});

        const standardPageHeader: ColorPairData = this.themeModel.getPaletteEntry("standard-page-header");
        if (standardPageHeader) {
            const metaTag: Element = document.querySelector("meta[name='theme-color']");
            metaTag.setAttribute("content", standardPageHeader.backgroundColor);
        }
    }

    public async back(): Promise<void> {
        this.onBack.emit();
        if (this.autoDismissOnBack()) {
            void this.navigationService.dismissView();
        }
    }

    // Used to clarify type after use of async in template
    appData(appData: AppData): AppData {
        return appData;
    }

    public pushScrollPosition(): void {
        if (this.pageScrollerER()) {
            this.scrollPositionStack.push(this.pageScrollerER().nativeElement.scrollTop);
            this.pageScrollerER().nativeElement.scrollTop = 0;
        }
    }

    public popScrollPosition(): void {
        if (this.scrollPositionStack.length) {

            // Interrupt any scrolling in progress else safari gets into a buggy state
            this.pageScrollerER().nativeElement.style.overflow = "hidden";
            setTimeout(() => {
                this.pageScrollerER().nativeElement.style.overflow = null;
                this.pageScrollerER().nativeElement.scrollTop = this.scrollPositionStack.pop();
            }, 50);
        }
    }

    public async fadeOutAndIn(): Promise<void> {
        this.pageScrollerER().nativeElement.classList.add("faded-out");
        await new Promise(resolve => setTimeout(resolve, 200));
        this.pageScrollerER().nativeElement.classList.remove("faded-out");
    }
}
